import React, {useState, useCallback, useMemo, useEffect} from 'react'
import * as PropTypes from 'prop-types'
import {InferPropTypes} from '@modifi/typescript-utils'
import {DropDownArrowIcon, RadioGroupPure, Row} from '@modifi/ui'
import {createNonce} from '@modifi/utilities'
import {useTranslation} from '@modifi/plugin-i18n'
import Cookies from 'js-cookie'
import {clientConfig} from '@modifi/client-config'
import {Divider} from '../../styles'
import {
  ApplyButton,
  DropdownHeader,
  DropdownWrapper,
  LRDropdown,
  RegionIconWithMR,
  StyledCol,
  StyledLabelRadio,
} from './styles'
import {
  useAlternativeRoutes,
  useCurrentLocale,
  useCurrentRegion,
} from '../../../../../features/route-info'
import {RegionIcon} from '../../../../../assets/icons'
import {StyledTwemoji} from '../../../../../features/sign-up/components/country-code-select-input/styles'
import {getFlagEmoji} from '../../../../../utils/getFlagEmoji'
import {updateAlternativePath, zendeskLocaleMap} from './utils'

const labelIconPropTypes = {
  region: PropTypes.string.isRequired,
}

const labelIconDefaultProps = {}

export type LabelIconProps = InferPropTypes<typeof labelIconPropTypes, typeof labelIconDefaultProps>

const LabelIcon: React.FC<LabelIconProps> = ({region}) =>
  region === 'global' ? (
    <RegionIconWithMR height={16} width={16} />
  ) : (
    <StyledTwemoji options={{base: 'https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/'}}>
      {getFlagEmoji(region)}
    </StyledTwemoji>
  )

LabelIcon.propTypes = labelIconPropTypes
LabelIcon.defaultProps = labelIconDefaultProps

const propTypes = {
  isHamburger: PropTypes.bool,
  regionAndLocaleMap: PropTypes.objectOf(
    PropTypes.objectOf(PropTypes.string.isRequired).isRequired
  ),
  redirect: PropTypes.func.isRequired,
  queryParams: PropTypes.string,
  routePathName: PropTypes.string,
}
const defaultProps = {
  isHamburger: undefined,
  regionAndLocaleMap: undefined,
  queryParams: '',
  routePathName: '',
}

export type RegionAndLocaleDropdownProps = InferPropTypes<typeof propTypes, typeof defaultProps>

const RegionAndLocaleDropdown: React.FC<RegionAndLocaleDropdownProps> = ({
  regionAndLocaleMap,
  isHamburger,
  redirect,
  queryParams,
  routePathName,
}) => {
  const {t} = useTranslation('janus')
  const [localNonce] = useState(createNonce())
  const currentRegion = useCurrentRegion()
  const currentLocale = useCurrentLocale()
  const alternativeRoutes = useAlternativeRoutes()

  const [isOpen, setIsOpen] = useState(isHamburger || false)
  const [selectedRegion, setSelectedRegion] = useState(currentRegion)
  const [selectedLocale, setSelectedLocale] = useState(currentLocale)

  useEffect(() => {
    if (
      typeof regionAndLocaleMap !== 'undefined' &&
      typeof selectedRegion !== 'undefined' &&
      typeof regionAndLocaleMap[selectedRegion] !== 'undefined' &&
      (typeof selectedLocale === 'undefined' ||
        Object.keys(regionAndLocaleMap[selectedRegion]).includes(selectedLocale) === false)
    ) {
      setSelectedLocale(Object.keys(regionAndLocaleMap[selectedRegion])[0])
    }
  }, [regionAndLocaleMap, selectedRegion, selectedLocale])

  useEffect(() => {
    setSelectedRegion(currentRegion)
  }, [currentRegion])

  useEffect(() => {
    setSelectedLocale(currentLocale)
  }, [currentLocale])

  const show = useCallback(() => {
    setIsOpen(true)
  }, [])
  const hide = useCallback(() => {
    setIsOpen(false)
    // reset again
    setSelectedRegion(currentRegion)
    setSelectedLocale(currentLocale)
  }, [currentRegion, currentLocale])

  useEffect(() => {
    // @ts-expect-error zE will exist if zendesk scripts load
    const zE = typeof window !== 'undefined' && window?.zE
    const zeLocales = (clientConfig.get('AVAILABLE_ZENDESK_LOCALES', '') as string).split(',')
    if (zE && currentLocale) {
      if (zeLocales.includes(currentLocale)) {
        const zendeskLocale = zendeskLocaleMap[currentLocale]
        zE('webWidget', 'show')
        zE('webWidget', 'setLocale', zendeskLocale)
      } else {
        zE('webWidget', 'hide')
      }
    }
  }, [currentLocale])

  const regionOptions = useMemo(() => {
    if (typeof regionAndLocaleMap === 'undefined') {
      return []
    }
    return Object.keys(regionAndLocaleMap).map(region => ({
      label: (
        <StyledLabelRadio>
          <LabelIcon region={region} />
          {t(`region.${region}`)}
        </StyledLabelRadio>
      ),
      value: region,
      colProps: {
        auto: true,
      },
    }))
  }, [regionAndLocaleMap, t])

  const localeOptions = useMemo(() => {
    if (
      typeof regionAndLocaleMap === 'undefined' ||
      typeof selectedRegion === 'undefined' ||
      typeof regionAndLocaleMap[selectedRegion] === 'undefined'
    ) {
      return []
    }
    return Object.keys(regionAndLocaleMap[selectedRegion]).map(locale => ({
      label: <StyledLabelRadio>{t(`locale.${locale}`)}</StyledLabelRadio>,
      value: locale,
      colProps: {
        auto: true,
      },
    }))
  }, [regionAndLocaleMap, selectedRegion, t])

  const handleChangeRegion = useCallback((e: unknown, val: {value: string}): void => {
    setSelectedRegion(val.value)
  }, [])

  const handleChangeLocale = useCallback((e: unknown, val: {value: string}): void => {
    setSelectedLocale(val.value)
  }, [])

  const handleSubmit = useCallback(() => {
    if (!selectedRegion || !selectedLocale) {
      return
    }
    // first lets store the selection in our cookies
    Cookies.set('modifi-region', selectedRegion, {
      sameSite: 'strict',
      expires: 30,
    })
    Cookies.set('modifi-locale', selectedLocale, {
      sameSite: 'strict',
      expires: 30,
    })

    // let's first try to navigate to the same page within a different region or locale
    const alternativePath = alternativeRoutes?.[selectedRegion]?.[selectedLocale]
    if (typeof alternativePath !== 'undefined') {
      redirect(`${updateAlternativePath(alternativePath, routePathName!)}?${queryParams}`)
    } else {
      const rootPath = regionAndLocaleMap?.[selectedRegion]?.[selectedLocale]
      if (typeof rootPath !== 'undefined') {
        redirect(`${rootPath}?${queryParams}`)
      }
    }

    window.scrollTo(0, 0)
  }, [
    selectedRegion,
    selectedLocale,
    alternativeRoutes,
    redirect,
    queryParams,
    regionAndLocaleMap,
    routePathName,
  ])

  return (
    <DropdownWrapper onMouseEnter={show} onMouseLeave={hide}>
      <DropdownHeader>
        <RegionIcon height={20} width={20} />
        &nbsp;
        <DropDownArrowIcon />
      </DropdownHeader>
      <LRDropdown isOpen={isOpen} isHamburger={isHamburger || false}>
        <Row justifyContent="center">
          <StyledCol>
            <h4>{t('region-locale-dropdown.region.label')}</h4>
            <RadioGroupPure
              name={`region-radio-group-${localNonce}`}
              options={regionOptions}
              value={selectedRegion}
              onChange={handleChangeRegion}
            />
          </StyledCol>

          <Divider ml={16} mr={16} height="auto" as="div" />
          <StyledCol>
            <h4>{t('region-locale-dropdown.language.label')}</h4>
            <RadioGroupPure
              name={`language-radio-group-${localNonce}`}
              options={localeOptions}
              value={selectedLocale}
              onChange={handleChangeLocale}
            />
          </StyledCol>
        </Row>
        <ApplyButton
          onClick={handleSubmit}
          data-target="language and region"
          style={{width: '100%'}}
        >
          {t('region-locale-dropdown.cta-submit.label')}
        </ApplyButton>
      </LRDropdown>
    </DropdownWrapper>
  )
}

RegionAndLocaleDropdown.propTypes = propTypes
RegionAndLocaleDropdown.defaultProps = defaultProps

export default React.memo(RegionAndLocaleDropdown)
